import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Space, Typography, theme, message, Spin, Alert, Tooltip, Table, Popconfirm, Tabs, Statistic, Row, Col, Divider, Modal, DatePicker, Form, Select } from "antd";
import {
  ReloadOutlined,
  CloudServerOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  LineChartOutlined,
  PoweroffOutlined,
  DollarOutlined,
  ScheduleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  StopOutlined,
  ClearOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  HistoryOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  HddOutlined,
  DashboardOutlined,
  RightOutlined,
  ApartmentOutlined,
  FundOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PageLayout from "../pageLayout/PageLayout.react";

const { Text } = Typography;

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

// Engine-specific accent colors (used for numbers, buttons, icons only)
const ENGINE_COLORS = {
  realtime: { primary: "#1890ff", tag: "blue" },
  dispatch: { primary: "#722ed1", tag: "purple" },
  settlement: { primary: "#d48806", tag: "gold" },
  bidding: { primary: "#6d3075", tag: "purple" },
  chatbot: { primary: "#eb2f96", tag: "magenta" },
  "aemo-settlement": { primary: "#13c2c2", tag: "cyan" },
  clustering: { primary: "#52c41a", tag: "green" },
  forecast: { primary: "#fa8c16", tag: "orange" },
};

// Tab ordering: first row = core engines, second row = auxiliary engines
const ENGINE_ORDER = ["realtime", "dispatch", "settlement", "bidding", "aemo-settlement", "chatbot", "clustering", "forecast"];

// AEMO logo icon for bidding
const AemoSwooshIcon = () => (
  <svg viewBox="0 0 98 88" width="1em" height="1em" fill="currentColor" stroke="none">
    <g transform="translate(0,88) scale(0.1,-0.1)">
      <path d="M590 846 c0 -2 18 -15 39 -29 27 -18 49 -46 70 -89 28 -56 32 -72 30 -138 -2 -62 -7 -85 -32 -130 -16 -30 -40 -68 -53 -83 -13 -16 -24 -34 -24 -40 1 -21 98 107 104 137 2 6 8 31 15 55 7 25 11 57 9 72 -3 15 -1 26 3 24 4 -3 12 2 19 10 11 13 10 14 -2 10 -9 -4 -18 1 -21 9 -4 9 -2 16 3 16 5 0 1 15 -9 33 -15 26 -16 34 -5 34 8 0 14 -7 14 -14 0 -8 4 -11 10 -8 12 7 52 -55 46 -72 -3 -8 1 -10 11 -6 12 5 15 2 10 -10 -3 -9 -2 -17 4 -17 14 0 31 -50 21 -64 -5 -6 -12 -50 -14 -97 -3 -47 -12 -97 -19 -110 -7 -13 -14 -26 -15 -29 -2 -12 -19 -46 -26 -55 -55 -69 -118 -158 -115 -161 6 -6 93 91 109 121 47 84 61 117 73 173 7 35 12 76 10 93 -3 17 0 27 5 24 6 -4 10 -21 10 -39 0 -43 -20 -148 -30 -161 -5 -5 -9 -14 -11 -20 -4 -20 -34 -80 -41 -83 -5 -2 -8 -9 -8 -15 -1 -7 -14 -27 -30 -46 -16 -19 -28 -36 -26 -38 7 -8 36 10 36 22 0 7 6 15 14 18 11 5 74 120 82 150 1 4 2 8 4 10 5 6 12 50 20 127 l9 85 -3 -112 c-3 -77 -8 -118 -17 -129 -8 -10 -13 -20 -13 -23 1 -3 -2 -15 -6 -26 -4 -11 -8 -24 -8 -28 -1 -4 -8 -15 -17 -23 -8 -9 -15 -21 -15 -27 1 -18 60 55 60 73 0 9 4 20 9 26 6 5 13 24 16 43 3 19 8 38 11 43 11 18 4 153 -11 203 -18 62 -79 157 -140 217 -52 52 -165 116 -165 94z"/>
      <path d="M345 632 c-39 -20 -85 -48 -103 -61 -18 -13 -35 -21 -38 -18 -6 6 54 51 109 83 23 13 35 24 26 24 -22 0 -126 -70 -173 -116 -21 -22 -50 -56 -63 -76 -29 -47 -40 -50 -17 -5 28 54 74 105 131 144 58 40 65 52 15 27 -47 -23 -138 -116 -158 -161 -25 -57 -27 -63 -28 -128 -1 -78 35 -139 107 -183 l50 -31 26 29 c14 15 45 40 69 54 23 14 42 29 42 31 0 11 -63 -28 -101 -62 -22 -20 -43 -33 -47 -29 -9 10 37 48 103 87 28 16 46 29 42 29 -17 0 -88 -42 -122 -71 -20 -18 -39 -29 -43 -25 -8 9 26 37 91 75 29 17 43 31 35 34 -8 3 -3 7 12 11 15 4 40 1 62 -9 21 -8 45 -15 55 -14 10 0 -4 9 -32 19 -27 10 -43 19 -34 19 9 1 35 -6 58 -14 23 -8 59 -14 79 -14 45 1 92 -8 109 -23 7 -6 13 -7 13 -2 0 5 5 2 11 -6 9 -12 10 -12 5 4 -9 27 -49 75 -63 75 -7 1 -13 6 -13 12 0 5 -4 7 -10 4 -5 -3 -10 -1 -10 4 0 6 -3 10 -7 10 -5 -1 -10 0 -13 1 -24 9 -65 12 -187 13 -79 1 -143 4 -142 7 2 20 172 118 244 140 22 7 45 17 50 22 16 16 -32 0 -124 -43 -46 -22 -86 -37 -89 -35 -2 3 10 12 27 21 17 8 31 18 31 21 0 2 -9 0 -21 -6 -11 -7 -23 -9 -26 -6 -8 7 102 60 180 86 67 23 88 35 45 26 -32 -7 -121 -42 -179 -71 -54 -28 -63 -30 -53 -14 4 6 48 31 98 56 50 24 80 41 66 37 -33 -9 -137 -61 -173 -86 -16 -11 -31 -18 -34 -15 -8 9 105 78 187 114 41 18 69 33 61 33 -41 0 -290 -139 -263 -148 7 -2 10 -8 6 -13 -3 -5 -1 -9 5 -9 5 0 13 5 16 10 3 6 11 10 17 10 6 0 6 -6 -2 -15 -11 -13 -10 -14 6 -9 18 6 17 5 -1 -9 -12 -10 -15 -16 -7 -17 7 0 10 -5 7 -10 -4 -6 -15 -8 -25 -5 -10 3 -21 1 -25 -5 -3 -5 0 -10 7 -10 10 0 9 -3 -2 -9 -8 -6 -17 -8 -21 -5 -4 2 -10 0 -14 -6 -4 -6 -10 -8 -15 -5 -4 3 -13 -2 -20 -10 -10 -12 -8 -13 10 -10 14 3 20 0 17 -8 -2 -7 -14 -11 -26 -11 -12 1 -19 -1 -16 -4 10 -11 40 -11 80 -1 25 6 67 7 112 1 48 -5 76 -5 83 2 7 7 10 6 10 -3 0 -7 4 -10 9 -7 5 3 14 -1 21 -9 7 -8 18 -15 24 -15 6 0 20 -7 30 -15 18 -14 17 -15 -25 -14 -32 0 -39 3 -24 8 19 8 1 10 -135 14 -35 1 -178 -58 -167 -69 3 -3 16 1 29 10 29 19 134 56 157 56 9 -1 -8 -9 -37 -20 -30 -10 -75 -31 -100 -46 -26 -15 -49 -27 -51 -26 -3 1 -6 12 -8 25 -3 21 4 28 44 45 82 35 135 44 191 34 28 -5 55 -6 61 -2 6 3 -34 12 -89 18 -91 11 -105 10 -150 -7 -28 -11 -50 -15 -50 -11 0 4 -7 11 -16 14 -13 5 -14 4 -6 -7 7 -8 9 -17 5 -20 -11 -12 11 -7 65 13 29 11 62 20 75 20 13 -1 2 -8 -28 -19 -28 -10 -61 -23 -73 -30 -18 -10 -27 -10 -38 -1 -7 7 -12 16 -9 20 10 16 -24 18 -37 2 -7 -8 -8 -13 -3 -10 23 13 47 -53 25 -67 -7 -4 -17 -2 -23 5 -9 9 -9 11 -1 7 16 -9 25 16 12 34 -8 11 -9 11 -4 -3 3 -12 1 -15 -10 -11 -7 3 -14 17 -14 32 0 30 24 70 36 58 4 -4 2 -7 -4 -7 -7 0 -12 -5 -12 -11 0 -8 4 -8 14 1 8 6 12 19 9 27 -4 9 -2 14 3 10 5 -3 10 3 10 13 0 10 -4 16 -8 14 -14 -9 -9 10 9 36 16 24 16 24 0 11 -9 -7 -17 -9 -17 -3 0 5 7 15 15 22 12 10 15 10 15 -1 0 -7 19 5 43 28 48 47 154 117 203 135 19 7 34 16 34 20 0 11 4 12 -75 -30z"/>
    </g>
  </svg>
);

function formatDuration(ms) {
  if (!ms || ms < 0) return "—";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// Strip ANSI escape codes from log messages
function stripAnsi(str) {
  if (!str) return str;
  // eslint-disable-next-line no-control-regex
  return str.replace(/\x1B(?:[@-Z\\-_]|\[[0-?]*[ -/]*[@-~])/g, "");
}

// Parse log line to extract components
function parseLogLine(logMsg) {
  const stripped = stripAnsi(logMsg || "");

  // Try to parse JSON log format (pino style)
  try {
    if (stripped.startsWith("{")) {
      const parsed = JSON.parse(stripped);
      return {
        level: parsed.level,
        levelName: getLevelName(parsed.level),
        pid: parsed.pid,
        title: parsed.name || parsed.hostname || "",
        msg: parsed.msg || "",
        extra: Object.keys(parsed)
          .filter(k => !["level", "time", "pid", "hostname", "name", "msg", "v"].includes(k))
          .reduce((acc, k) => ({ ...acc, [k]: parsed[k] }), {}),
        raw: parsed,
      };
    }
  } catch (e) {
    // Not JSON, try regex parsing
  }

  // Try to match common log patterns
  const match = stripped.match(/^\[([^\]]+)\]\s*(?:\[([^\]]+)\])?\s*(?:\[([^\]]+)\])?\s*(.*)/);
  if (match) {
    return {
      levelName: match[2] || "",
      title: match[3] || "",
      msg: match[4] || stripped,
      extra: {},
      raw: null,
    };
  }

  return {
    levelName: "",
    title: "",
    msg: stripped,
    extra: {},
    raw: null,
  };
}

function getLevelName(level) {
  if (level >= 60) return "fatal";
  if (level >= 50) return "error";
  if (level >= 40) return "warn";
  if (level >= 30) return "info";
  if (level >= 20) return "debug";
  return "trace";
}

function getLevelColor(levelName) {
  const colors = {
    fatal: "#ff4d4f",
    error: "#ff6b6b",
    warn: "#faad14",
    info: "#52c41a",
    debug: "#1890ff",
    trace: "#8c8c8c",
  };
  return colors[levelName] || "#98c379";
}

// Queue step info for progress estimation
const queueStepInfo = {
  "re-system-ingest": { step: "1/2", stepName: "Ingest", estimatedMs: 30000 },
  "re-group-realtime": { step: "2/2", stepName: "Group Update", estimatedMs: 5000 },
  "re-system-aggregation": { step: "1/1", stepName: "Sys Aggregation", estimatedMs: 60000 },
  "re-group-aggregation": { step: "1/1", stepName: "Grp Aggregation", estimatedMs: 30000 },
  "de-group-command": { step: "1/3", stepName: "Command", estimatedMs: 10000 },
  "de-device-allocation": { step: "2/3", stepName: "Allocation", estimatedMs: 5000 },
  "de-device-execute": { step: "3/3", stepName: "Execute", estimatedMs: 15000 },
  "se-load-run": { step: "1/4", stepName: "Load", estimatedMs: 5000 },
  "se-group-revenue": { step: "2/4", stepName: "Revenue", estimatedMs: 30000 },
  "se-system-allocation": { step: "3/4", stepName: "Sys Alloc", estimatedMs: 20000 },
  "se-company-customer": { step: "4/4", stepName: "Settle", estimatedMs: 30000 },
  "be-evaluate": { step: "1/3", stepName: "Evaluate", estimatedMs: 5000 },
  "be-generate-bid": { step: "2/3", stepName: "Generate", estimatedMs: 10000 },
  "be-submit-bid": { step: "3/3", stepName: "Submit", estimatedMs: 15000 },
  "cb-responder": { step: "1/1", stepName: "Respond", estimatedMs: 30000 },
};

export default function EngineStatus() {
  const navigate = useNavigate();
  const [engineHealth, setEngineHealth] = useState(null);
  const [engineLogs, setEngineLogs] = useState({});
  const [engineJobHistory, setEngineJobHistory] = useState({});
  const [redisInfo, setRedisInfo] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [serverTimezone, setServerTimezone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState({});
  const [cancellingJobs, setCancellingJobs] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [queueFilter, setQueueFilter] = useState(null);
  const [jobTabKey, setJobTabKey] = useState("active"); // "active", "waiting", "completed", "failed"
  const { token } = theme.useToken();

  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState({});
  const [schedulerLoading, setSchedulerLoading] = useState({});

  // Aggregation modal
  const [aggregationModalOpen, setAggregationModalOpen] = useState(false);
  const [aggregationDate, setAggregationDate] = useState(null);

  // Backfill modal
  const [backfillModalOpen, setBackfillModalOpen] = useState(false);
  const [backfillForm] = Form.useForm();
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [systemOptions, setSystemOptions] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);

  // AEMO Settlement modal
  const [aemoSettlementModalOpen, setAemoSettlementModalOpen] = useState(false);
  const [aemoSettlementDate, setAemoSettlementDate] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const authToken = localStorage.getItem("token");
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const engineResp = await fetch(`${API_BASE}/api/engine/health`, {
        headers: getAuthHeaders(),
      });
      if (!engineResp.ok) throw new Error(`HTTP ${engineResp.status}`);
      const engineData = await engineResp.json();
      setEngineHealth(engineData.engines || []);
      setServerTime(engineData.serverTime || null);
      setServerTimezone(engineData.serverTimezone || null);

      if (!activeTab && engineData.engines?.length > 0) {
        setActiveTab(engineData.engines[0].name);
      }

      try {
        const queueResp = await fetch(`${API_BASE}/api/queues/status`);
        if (queueResp.ok) {
          const queueData = await queueResp.json();
          setRedisInfo(queueData.redisInfo || null);
        }
      } catch (e) {
        // Redis info is optional
      }
    } catch (err) {
      setError(err.message);
      setEngineHealth(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, activeTab]);

  const fetchLogs = useCallback(async (engineName) => {
    try {
      const resp = await fetch(`${API_BASE}/api/engine/${engineName}/logs`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setEngineLogs((prev) => ({ ...prev, [engineName]: data }));
      }
    } catch (err) {
      console.error(`Failed to fetch logs for ${engineName}:`, err);
    }
  }, [getAuthHeaders]);

  const fetchJobHistory = useCallback(async (engineName, status) => {
    try {
      const resp = await fetch(`${API_BASE}/api/engine/${engineName}/jobs?status=${status}&limit=30`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setEngineJobHistory((prev) => ({
          ...prev,
          [engineName]: {
            ...prev[engineName],
            [status]: data.jobs || [],
          },
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${status} jobs for ${engineName}:`, err);
    }
  }, [getAuthHeaders]);

  const fetchSchedulerStatus = useCallback(async () => {
    const engines = ["realtime", "dispatch"];
    try {
      const results = await Promise.all(
        engines.map(async (engine) => {
          try {
            const resp = await fetch(`${API_BASE}/api/engine/${engine}/scheduler/status`, {
              headers: getAuthHeaders(),
            });
            if (resp.ok) {
              const data = await resp.json();
              return { engine, running: data.running };
            }
          } catch {
            // Individual engine status fetch failed
          }
          return null;
        })
      );
      results.forEach((result) => {
        if (result) {
          setSchedulerStatus((prev) => ({ ...prev, [result.engine]: result.running }));
        }
      });
    } catch (err) {
      // Scheduler status fetch failed silently
    }
  }, [getAuthHeaders]);

  const fetchSystems = useCallback(async () => {
    setSystemsLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/systems`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const result = await resp.json();
        const systems = result.data || [];
        const options = systems.map((sys) => ({
          value: sys.sn,
          label: `${sys.sn} - ${sys.customer_name || sys.nmi || "Unknown"}`,
        }));
        setSystemOptions(options);
      }
    } catch (err) {
      console.error("Failed to fetch systems:", err);
    } finally {
      setSystemsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
    fetchSchedulerStatus();
    const timer = setInterval(() => {
      fetchStatus();
      fetchSchedulerStatus();
    }, 10000);
    return () => clearInterval(timer);
  }, [fetchStatus, fetchSchedulerStatus]);

  // Fetch logs for active tab
  useEffect(() => {
    if (activeTab) {
      fetchLogs(activeTab);
      const logTimer = setInterval(() => fetchLogs(activeTab), 3000);
      return () => clearInterval(logTimer);
    }
  }, [activeTab, fetchLogs]);

  // Fetch job history when job tab changes or active engine changes
  useEffect(() => {
    if (activeTab && (jobTabKey === "completed" || jobTabKey === "failed")) {
      fetchJobHistory(activeTab, jobTabKey);
    }
  }, [activeTab, jobTabKey, fetchJobHistory]);

  // Fetch completed and failed job counts for badge display on initial load
  useEffect(() => {
    if (activeTab) {
      fetchJobHistory(activeTab, "completed");
      fetchJobHistory(activeTab, "failed");
    }
  }, [activeTab, fetchJobHistory]);

  const getEngineIcon = (name) => {
    const icons = {
      realtime: <LineChartOutlined />,
      dispatch: <ThunderboltOutlined />,
      settlement: <DollarOutlined />,
      bidding: <AemoSwooshIcon />,
      chatbot: <RobotOutlined />,
      "aemo-settlement": <AuditOutlined />,
      clustering: <ApartmentOutlined />,
      forecast: <FundOutlined />,
    };
    return icons[name] || <CloudServerOutlined />;
  };

  const restartEngine = async (engineName) => {
    setTriggerLoading((prev) => ({ ...prev, [`restart_${engineName}`]: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/${engineName}/restart`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      message.success(data.message);
      setTimeout(fetchStatus, 5000);
    } catch (err) {
      message.error(`Failed to restart: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, [`restart_${engineName}`]: false }));
    }
  };

  const toggleScheduler = async (engine) => {
    const isRunning = schedulerStatus[engine];
    const action = isRunning ? "pause" : "start";
    setSchedulerLoading((prev) => ({ ...prev, [engine]: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/${engine}/scheduler/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setSchedulerStatus((prev) => ({ ...prev, [engine]: data.running }));
      message.success(data.message || `Scheduler ${action}ed`);
    } catch (err) {
      message.error(`Failed to ${action} scheduler: ${err.message}`);
    } finally {
      setSchedulerLoading((prev) => ({ ...prev, [engine]: false }));
    }
  };

  const triggerPoll = async () => {
    setTriggerLoading((prev) => ({ ...prev, poll: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/realtime/trigger-poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      message.success(`Poll triggered: ${data.jobId || "OK"}`);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, poll: false }));
    }
  };

  const triggerAggregation = async () => {
    if (!aggregationDate) {
      message.warning("Please select a date");
      return;
    }
    setTriggerLoading((prev) => ({ ...prev, aggregation: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/realtime/trigger-aggregation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ date: aggregationDate.format("YYYY-MM-DD") }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      message.success(`Aggregation triggered: ${data.jobIds?.length || 1} jobs`);
      setAggregationModalOpen(false);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, aggregation: false }));
    }
  };

  const openBackfillModal = () => {
    fetchSystems();
    backfillForm.resetFields();
    const now = dayjs();
    backfillForm.setFieldsValue({
      timeRange: [now.subtract(1, "hour"), now],
    });
    setBackfillModalOpen(true);
  };

  const submitBackfill = async (values) => {
    const { sns, timeRange } = values;
    if (!sns || sns.length === 0) {
      message.error("Please select at least one system");
      return;
    }
    if (!timeRange || timeRange.length !== 2) {
      message.error("Please select a time range");
      return;
    }

    const [start, end] = timeRange;
    const diffHours = end.diff(start, "hour", true);
    if (diffHours > 24) {
      message.error("Time range cannot exceed 24 hours");
      return;
    }
    if (diffHours <= 0) {
      message.error("End time must be after start time");
      return;
    }

    setBackfillLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/engine/realtime/trigger-backfill`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          sns,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      message.success(`Backfill job queued: ${data.jobId || "OK"}`);
      setBackfillModalOpen(false);
      fetchStatus();
    } catch (err) {
      message.error(`Backfill failed: ${err.message}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  const triggerDispatchCycle = async () => {
    setTriggerLoading((prev) => ({ ...prev, dispatchCycle: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/dispatch/trigger`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      message.success(data.message || "Dispatch cycle triggered");
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger dispatch cycle: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, dispatchCycle: false }));
    }
  };

  const triggerBidding = async () => {
    setTriggerLoading((prev) => ({ ...prev, bidding: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/bidding/trigger`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      message.success(`Bidding triggered: ${data.jobId || "OK"}`);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, bidding: false }));
    }
  };

  const triggerChatbot = async () => {
    setTriggerLoading((prev) => ({ ...prev, chatbot: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/chatbot/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ communicationId: "test" }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      message.success(`Chatbot triggered: ${data.jobId || "OK"}`);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, chatbot: false }));
    }
  };

  const triggerAemoSettlement = async () => {
    if (!aemoSettlementDate) {
      message.warning("Please select a settlement date");
      return;
    }
    setTriggerLoading((prev) => ({ ...prev, aemoSettlement: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/aemo-settlement/trigger`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ settlement_date: aemoSettlementDate.format("YYYY-MM-DD") }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      message.success(`AEMO sync triggered: ${data.data?.configsTriggered || 0} configs`);
      setAemoSettlementModalOpen(false);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to trigger: ${err.message}`);
    } finally {
      setTriggerLoading((prev) => ({ ...prev, aemoSettlement: false }));
    }
  };

  const cancelJob = async (queueName, jobId, force = false) => {
    const key = `${queueName}:${jobId}`;
    setCancellingJobs((prev) => ({ ...prev, [key]: true }));
    try {
      const url = force
        ? `${API_BASE}/api/engine/jobs/${queueName}/${jobId}/cancel?force=true`
        : `${API_BASE}/api/engine/jobs/${queueName}/${jobId}/cancel`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ reason: force ? "Force cancelled by admin via UI" : "Cancelled by admin via UI" }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      message.success(data.message || `Job ${jobId} cancelled`);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to cancel job: ${err.message}`);
    } finally {
      setCancellingJobs((prev) => ({ ...prev, [key]: false }));
    }
  };

  const drainQueue = async (queueName) => {
    const key = `drain:${queueName}`;
    setCancellingJobs((prev) => ({ ...prev, [key]: true }));
    try {
      const resp = await fetch(`${API_BASE}/api/engine/queues/${queueName}/drain`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      message.success(data.message || `Queue ${queueName} drained`);
      fetchStatus();
    } catch (err) {
      message.error(`Failed to drain queue: ${err.message}`);
    } finally {
      setCancellingJobs((prev) => ({ ...prev, [key]: false }));
    }
  };

  const formatLogTime = (isoTime) => {
    if (!isoTime) return "—";
    const date = new Date(isoTime);
    if (serverTimezone) {
      try {
        return date.toLocaleTimeString("en-AU", { timeZone: serverTimezone, hour12: false });
      } catch (e) {
        // Fallback
      }
    }
    return date.toISOString().slice(11, 19);
  };

  // Format for Last Activity scorecard - compact date+time in server timezone
  const formatActivityTime = (isoTime) => {
    if (!isoTime) return "—";
    const date = new Date(isoTime);
    if (serverTimezone) {
      try {
        // Compact format: "20/02 15:30:45"
        return date.toLocaleString("en-AU", {
          timeZone: serverTimezone,
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).replace(",", "");
      } catch (e) {
        // Fallback
      }
    }
    return date.toISOString().slice(5, 19).replace("T", " ");
  };

  // Get engine-specific trigger buttons
  const getEngineTriggerButtons = (engineName) => {
    const colors = ENGINE_COLORS[engineName] || ENGINE_COLORS.realtime;

    switch (engineName) {
      case "realtime":
        return (
          <Space wrap>
            <Button
              type={schedulerStatus.realtime ? "default" : "primary"}
              icon={schedulerStatus.realtime ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              loading={schedulerLoading.realtime}
              onClick={() => toggleScheduler("realtime")}
              style={{ borderColor: colors.primary, color: schedulerStatus.realtime ? colors.primary : undefined }}
            >
              {schedulerStatus.realtime ? "Pause Scheduler" : "Start Scheduler"}
            </Button>
            <Button
              icon={<SyncOutlined />}
              loading={triggerLoading.poll}
              onClick={triggerPoll}
            >
              Trigger Poll
            </Button>
            <Button
              icon={<ScheduleOutlined />}
              onClick={() => setAggregationModalOpen(true)}
            >
              Aggregation
            </Button>
            <Button
              icon={<HistoryOutlined />}
              onClick={openBackfillModal}
            >
              Backfill
            </Button>
          </Space>
        );
      case "dispatch":
        return (
          <Space wrap>
            <Button
              type={schedulerStatus.dispatch ? "default" : "primary"}
              icon={schedulerStatus.dispatch ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              loading={schedulerLoading.dispatch}
              onClick={() => toggleScheduler("dispatch")}
              style={{ borderColor: colors.primary, color: schedulerStatus.dispatch ? colors.primary : undefined }}
            >
              {schedulerStatus.dispatch ? "Pause Scheduler" : "Start Scheduler"}
            </Button>
            <Button
              icon={<ThunderboltOutlined />}
              loading={triggerLoading.dispatchCycle}
              onClick={triggerDispatchCycle}
            >
              Trigger Cycle
            </Button>
          </Space>
        );
      case "bidding":
        return (
          <Space wrap>
            <Button
              icon={<PlayCircleOutlined />}
              loading={triggerLoading.bidding}
              onClick={triggerBidding}
            >
              Trigger Bidding
            </Button>
          </Space>
        );
      case "chatbot":
        return (
          <Space wrap>
            <Button
              icon={<PlayCircleOutlined />}
              loading={triggerLoading.chatbot}
              onClick={triggerChatbot}
            >
              Trigger Generate
            </Button>
          </Space>
        );
      case "aemo-settlement":
        return (
          <Space wrap>
            <Button
              icon={<SyncOutlined />}
              onClick={() => {
                setAemoSettlementDate(dayjs().subtract(1, "day"));
                setAemoSettlementModalOpen(true);
              }}
            >
              Trigger Sync
            </Button>
          </Space>
        );
      case "clustering":
        return (
          <Space wrap>
            <Button
              type="primary"
              icon={<ApartmentOutlined />}
              loading={triggerLoading.clusteringFull}
              onClick={async () => {
                setTriggerLoading((prev) => ({ ...prev, clusteringFull: true }));
                try {
                  const resp = await fetch(`${API_BASE}/api/engine/clustering/trigger`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
                    body: JSON.stringify({ mode: "FULL" }),
                  });
                  const data = await resp.json();
                  if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
                  message.success(`Clustering FULL run enqueued (${data.runId})`);
                  fetchStatus();
                } catch (err) {
                  message.error(`Failed: ${err.message}`);
                } finally {
                  setTriggerLoading((prev) => ({ ...prev, clusteringFull: false }));
                }
              }}
            >
              Full Re-cluster
            </Button>
          </Space>
        );
      default:
        return null;
    }
  };

  // Build tab items (sorted by ENGINE_ORDER)
  const tabItems = useMemo(() => {
    if (!engineHealth) return [];

    const sorted = [...engineHealth].sort((a, b) => {
      const ia = ENGINE_ORDER.indexOf(a.name);
      const ib = ENGINE_ORDER.indexOf(b.name);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    const ROW_BREAK_AFTER = 5; // First row: Realtime, Dispatch, Settlement, Bidding, AEMO Settlement

    const items = sorted.map((engine) => {
      const logData = engineLogs[engine.name];
      const logs = logData?.logs || [];
      const isRunning = engine.processCount > 0 || engine.managed;
      const colors = ENGINE_COLORS[engine.name] || ENGINE_COLORS.realtime;

      // Calculate queue stats
      const queueStats = Object.entries(engine.queueDetails || {}).map(([queueName, stats]) => ({
        queueName,
        ...stats,
      }));

      const totalActive = queueStats.reduce((sum, q) => sum + (q.active || 0), 0);
      const totalWaiting = queueStats.reduce((sum, q) => sum + (q.waiting || 0), 0);
      const totalCompleted = queueStats.reduce((sum, q) => sum + (q.completed || 0), 0);
      const totalFailed = queueStats.reduce((sum, q) => sum + (q.failed || 0), 0);

      // Get active/waiting job details
      const activeJobs = (engine.activeJobDetails || []).filter(job => !queueFilter || job.queue === queueFilter);
      const waitingJobs = (engine.waitingJobDetails || []).filter(job => !queueFilter || job.queue === queueFilter);
      const completedJobs = (engineJobHistory[engine.name]?.completed || []).filter(job => !queueFilter || job.queue === queueFilter);
      const failedJobs = (engineJobHistory[engine.name]?.failed || []).filter(job => !queueFilter || job.queue === queueFilter);

      // Use queue totals for badge display when job history not yet loaded
      const completedBadgeCount = completedJobs.length > 0 ? completedJobs.length : (queueFilter ? 0 : totalCompleted);
      const failedBadgeCount = failedJobs.length > 0 ? failedJobs.length : (queueFilter ? 0 : totalFailed);

      // Build job tabs
      const jobTabItems = [
        {
          key: "active",
          label: <span>Active <Tag color={totalActive > 0 ? "green" : "default"}>{activeJobs.length}</Tag></span>,
          children: (
            <Table
              dataSource={activeJobs.map((job, idx) => {
                const now = Date.now();
                const startTime = job.processedOn ? new Date(job.processedOn).getTime() : now;
                const elapsed = now - startTime;
                const stepInfo = queueStepInfo[job.queue] || { step: "—", stepName: "Processing", estimatedMs: 30000 };
                const progress = job.progress || 0;
                const estimatedTotal = stepInfo.estimatedMs;
                const estimatedRemaining = progress > 0 ? (elapsed / progress) * (100 - progress) : Math.max(0, estimatedTotal - elapsed);
                const estimatedFinish = new Date(now + estimatedRemaining);
                return { key: job.id || idx, jobId: job.id, jobName: job.name || job.id, queue: job.queue, step: stepInfo.step, stepName: stepInfo.stepName, summary: job.summary, startTime: job.processedOn, elapsed, progress, estimatedFinish };
              })}
              columns={[
                { title: "Job", dataIndex: "jobName", key: "jobName", width: 150, ellipsis: true },
                { title: "Queue", dataIndex: "queue", key: "queue", width: 120, render: (q) => <Tag color={colors.tag}>{q.split("-").slice(1).join("-")}</Tag> },
                { title: "Step", dataIndex: "step", key: "step", width: 60, render: (s, r) => <Tooltip title={r.stepName}><Text>{s}</Text></Tooltip> },
                { title: "Description", dataIndex: "summary", key: "summary", ellipsis: true },
                { title: "Started", dataIndex: "startTime", key: "startTime", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Running", dataIndex: "elapsed", key: "elapsed", width: 70, render: (ms) => formatDuration(ms) },
                { title: "%", dataIndex: "progress", key: "progress", width: 50, render: (p) => p > 0 ? `${Math.round(p)}%` : "—" },
                {
                  title: "", key: "action", width: 50,
                  render: (_, record) => (
                    <Popconfirm title="Force stop?" description="Forcibly terminate this job" onConfirm={() => cancelJob(record.queue, record.jobId, true)} okText="Force Stop" okButtonProps={{ danger: true }}>
                      <Button size="small" type="text" danger icon={<StopOutlined />} loading={cancellingJobs[`${record.queue}:${record.jobId}`]} />
                    </Popconfirm>
                  ),
                },
              ]}
              size="small"
              pagination={false}
              locale={{ emptyText: "No active jobs" }}
            />
          ),
        },
        {
          key: "waiting",
          label: <span>Waiting <Tag color={totalWaiting > 0 ? "orange" : "default"}>{waitingJobs.length}</Tag></span>,
          children: (
            <Table
              dataSource={waitingJobs.slice(0, 20).map((job, idx) => {
                const now = Date.now();
                const queuedTime = job.timestamp ? new Date(job.timestamp).getTime() : now;
                const waitTime = now - queuedTime;
                const stepInfo = queueStepInfo[job.queue] || { step: "—", stepName: "Processing" };
                return { key: job.id || idx, jobId: job.id, queue: job.queue, step: stepInfo.step, stepName: stepInfo.stepName, summary: job.summary, queuedTime: job.timestamp, waitTime };
              })}
              columns={[
                { title: "Queue", dataIndex: "queue", key: "queue", width: 120, render: (q) => <Tag color="orange">{q.split("-").slice(1).join("-")}</Tag> },
                { title: "Step", dataIndex: "step", key: "step", width: 60, render: (s, r) => <Tooltip title={r.stepName}><Text>{s}</Text></Tooltip> },
                { title: "Description", dataIndex: "summary", key: "summary", ellipsis: true },
                { title: "Queued At", dataIndex: "queuedTime", key: "queuedTime", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Waiting", dataIndex: "waitTime", key: "waitTime", width: 80, render: (ms) => formatDuration(ms) },
                {
                  title: "", key: "action", width: 50,
                  render: (_, record) => (
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={cancellingJobs[`${record.queue}:${record.jobId}`]} onClick={() => cancelJob(record.queue, record.jobId)} />
                  ),
                },
              ]}
              size="small"
              pagination={false}
              locale={{ emptyText: "No waiting jobs" }}
            />
          ),
        },
        {
          key: "completed",
          label: <span>Completed <Tag>{completedBadgeCount}</Tag></span>,
          children: (
            <Table
              dataSource={completedJobs.map((job, idx) => {
                const duration = job.finishedOn && job.processedOn ? job.finishedOn - job.processedOn : null;
                const stepInfo = queueStepInfo[job.queue] || { step: "—", stepName: "Completed" };
                return {
                  key: job.id || idx,
                  jobId: job.id,
                  queue: job.queue,
                  step: stepInfo.step,
                  stepName: stepInfo.stepName,
                  summary: job.summary,
                  startedAt: job.processedOn,
                  finishedAt: job.finishedOn,
                  duration,
                };
              })}
              columns={[
                { title: "Job", dataIndex: "jobId", key: "jobId", width: 150, ellipsis: true },
                { title: "Queue", dataIndex: "queue", key: "queue", width: 120, render: (q) => <Tag>{q.split("-").slice(1).join("-")}</Tag> },
                { title: "Description", dataIndex: "summary", key: "summary", ellipsis: true },
                { title: "Started", dataIndex: "startedAt", key: "startedAt", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Finished", dataIndex: "finishedAt", key: "finishedAt", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Duration", dataIndex: "duration", key: "duration", width: 80, render: (ms) => formatDuration(ms) },
              ]}
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: "No completed jobs" }}
            />
          ),
        },
        {
          key: "failed",
          label: <span>Failed <Tag color={failedBadgeCount > 0 ? "red" : "default"}>{failedBadgeCount}</Tag></span>,
          children: (
            <Table
              dataSource={failedJobs.map((job, idx) => {
                const duration = job.finishedOn && job.processedOn ? job.finishedOn - job.processedOn : null;
                const stepInfo = queueStepInfo[job.queue] || { step: "—", stepName: "Failed" };
                return {
                  key: job.id || idx,
                  jobId: job.id,
                  queue: job.queue,
                  step: stepInfo.step,
                  stepName: stepInfo.stepName,
                  summary: job.summary,
                  startedAt: job.processedOn,
                  finishedAt: job.finishedOn,
                  duration,
                  failedReason: job.failedReason,
                  attemptsMade: job.attemptsMade,
                };
              })}
              columns={[
                { title: "Job", dataIndex: "jobId", key: "jobId", width: 150, ellipsis: true },
                { title: "Queue", dataIndex: "queue", key: "queue", width: 120, render: (q) => <Tag color="red">{q.split("-").slice(1).join("-")}</Tag> },
                { title: "Description", dataIndex: "summary", key: "summary", ellipsis: true },
                { title: "Started", dataIndex: "startedAt", key: "startedAt", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Failed", dataIndex: "finishedAt", key: "finishedAt", width: 140, render: (ts) => ts ? new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "") : "—" },
                { title: "Attempts", dataIndex: "attemptsMade", key: "attemptsMade", width: 70, render: (v) => v || 1 },
                {
                  title: "Error", dataIndex: "failedReason", key: "failedReason", ellipsis: true,
                  render: (v) => v ? <Text type="danger" style={{ fontSize: 11 }}>{v}</Text> : "—",
                },
              ]}
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: "No failed jobs" }}
            />
          ),
        },
      ];

      return {
        key: engine.name,
        label: (
          <Space size={6}>
            <span style={{ color: colors.primary, display: "flex", alignItems: "center", fontSize: 16 }}>
              {getEngineIcon(engine.name)}
            </span>
            <span style={{ fontWeight: 500 }}>{engine.label}</span>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isRunning ? "#52c41a" : "#d9d9d9",
            }} />
          </Space>
        ),
        children: (
          <div style={{ padding: 16 }}>
            {/* Engine Control Section */}
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={
                <Space>
                  <span style={{ color: colors.primary }}>{getEngineIcon(engine.name)}</span>
                  <span>Engine Control</span>
                </Space>
              }
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <Space size="middle">
                  <Button
                    icon={<SyncOutlined />}
                    loading={triggerLoading[`restart_${engine.name}`]}
                    onClick={() => restartEngine(engine.name)}
                    size="large"
                    disabled={!isRunning}
                  >
                    Restart Engine
                  </Button>
                  {(engine.managedPid || engine.pids?.length > 0) && (
                    <Text type="secondary">PID: {engine.managedPid || engine.pids?.join(", ")}{engine.hostname ? ` @ ${engine.hostname}` : ""}</Text>
                  )}
                </Space>

                <Divider type="vertical" style={{ height: 40 }} />

                {/* Engine-specific triggers */}
                {getEngineTriggerButtons(engine.name)}
              </div>
            </Card>

            {/* Scorecards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ textAlign: "center" }}>
                  <Statistic
                    title={<><TeamOutlined style={{ color: colors.primary }} /> Workers</>}
                    value={engine.workerCount}
                    valueStyle={{ color: engine.workerCount > 0 ? colors.primary : token.colorTextSecondary, fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ textAlign: "center" }}>
                  <Statistic
                    title={<><SyncOutlined spin={totalActive > 0} style={{ color: colors.primary }} /> Active Jobs</>}
                    value={totalActive}
                    valueStyle={{ color: totalActive > 0 ? colors.primary : token.colorTextSecondary, fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ textAlign: "center" }}>
                  <Statistic
                    title={<><PauseCircleOutlined /> Waiting Jobs</>}
                    value={totalWaiting}
                    valueStyle={{ color: totalWaiting > 0 ? token.colorWarning : token.colorTextSecondary, fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ textAlign: "center" }}>
                  <Statistic
                    title={<><ClockCircleOutlined /> Last Activity</>}
                    value={engine.lastActivity ? formatActivityTime(engine.lastActivity) : "—"}
                    valueStyle={{ color: token.colorTextSecondary, fontSize: 24 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Queue Stats */}
            {queueStats.length > 0 && (
              <Card
                size="small"
                style={{ marginBottom: 16 }}
                title={
                  <Space>
                    <span>Queue Stats</span>
                    {queueFilter && <Tag color={colors.tag} closable onClose={() => setQueueFilter(null)}>Filter: {queueFilter.split("-").slice(1).join("-")}</Tag>}
                  </Space>
                }
              >
                <Table
                  dataSource={queueStats}
                  rowKey="queueName"
                  size="small"
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => setQueueFilter(queueFilter === record.queueName ? null : record.queueName),
                    style: {
                      cursor: "pointer",
                      background: queueFilter === record.queueName ? token.colorBgTextHover : undefined,
                    },
                  })}
                  columns={[
                    {
                      title: "Queue",
                      dataIndex: "queueName",
                      key: "queueName",
                      render: (name) => (
                        <Tag color={queueFilter === name ? colors.tag : "default"}>
                          {name.split("-").slice(1).join("-")}
                        </Tag>
                      ),
                    },
                    { title: "Active", dataIndex: "active", key: "active", width: 80, render: (v) => <Text style={{ color: v > 0 ? colors.primary : undefined }}>{v}</Text> },
                    { title: "Waiting", dataIndex: "waiting", key: "waiting", width: 80, render: (v) => <Text type={v > 0 ? "warning" : "secondary"}>{v}</Text> },
                    { title: "Completed", dataIndex: "completed", key: "completed", width: 100, render: (v) => <Text type="secondary">{v}</Text> },
                    { title: "Failed", dataIndex: "failed", key: "failed", width: 80, render: (v) => <Text type={v > 0 ? "danger" : "secondary"}>{v}</Text> },
                    {
                      title: "",
                      key: "action",
                      width: 80,
                      render: (_, record) => (
                        <Tooltip title="Clear waiting jobs">
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<ClearOutlined />}
                            disabled={record.waiting === 0}
                            loading={cancellingJobs[`drain:${record.queueName}`]}
                            onClick={(e) => { e.stopPropagation(); drainQueue(record.queueName); }}
                          />
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Card>
            )}

            {/* Jobs Section with 4 Tabs */}
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title="Jobs"
              styles={{ body: { padding: 0 } }}
            >
              <Tabs
                activeKey={jobTabKey}
                onChange={setJobTabKey}
                items={jobTabItems}
                style={{ padding: "0 16px 16px" }}
              />
            </Card>

            {/* Logs Section */}
            <Card
              size="small"
              title={
                <Space>
                  <span>Logs</span>
                  {logs.length > 0 && <Tag>{logs.length}</Tag>}
                  {logData?.exitCode !== undefined && <Tag color="red">Exit: {logData.exitCode}</Tag>}
                </Space>
              }
            >
              {logData?.exitCode !== undefined && (
                <Alert
                  type="error"
                  message={`Process exited with code ${logData.exitCode}${logData.exitSignal ? `, signal ${logData.exitSignal}` : ""}${logData.error ? ` - ${logData.error}` : ""}`}
                  style={{ marginBottom: 12 }}
                />
              )}
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: 4,
                  padding: 12,
                  maxHeight: 400,
                  overflow: "auto",
                  fontFamily: "Monaco, Consolas, monospace",
                  fontSize: 12,
                }}
              >
                {logs.length > 0 ? (
                  logs.map((log, idx) => {
                    const parsed = parseLogLine(log.msg);
                    const hasExtra = Object.keys(parsed.extra).length > 0;

                    return (
                      <div key={idx} style={{ marginBottom: 6 }}>
                        <div style={{ color: log.type === "stderr" ? "#ff6b6b" : getLevelColor(parsed.levelName) }}>
                          <span style={{ color: "#5c6370" }}>{formatLogTime(log.time)}</span>
                          {parsed.pid && <span style={{ color: "#5c6370" }}> [{parsed.pid}]</span>}
                          {parsed.levelName && <span style={{ color: getLevelColor(parsed.levelName), marginLeft: 8 }}>[{parsed.levelName.toUpperCase()}]</span>}
                          {parsed.title && <span style={{ color: "#61afef", marginLeft: 8 }}>[{parsed.title}]</span>}
                        </div>
                        <div style={{ paddingLeft: 16, color: "#abb2bf", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {parsed.msg}
                        </div>
                        {hasExtra && (
                          <div style={{ paddingLeft: 24, color: "#5c6370", fontSize: 11 }}>
                            {Object.entries(parsed.extra).map(([k, v]) => (
                              <span key={k} style={{ marginRight: 12 }}>{k}={typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <Text style={{ color: "#5c6370" }}>
                    {logData?.exitCode !== undefined
                      ? "No output captured before exit."
                      : isRunning
                        ? "Engine is running remotely. Logs are available on the engine server."
                        : "Engine is not running."}
                  </Text>
                )}
              </div>
            </Card>
          </div>
        ),
      };
    });

    // Insert invisible row-break spacer after the first row of tabs
    if (items.length > ROW_BREAK_AFTER) {
      items.splice(ROW_BREAK_AFTER, 0, {
        key: "__row-break__",
        label: <span style={{ display: "none" }} />,
        disabled: true,
        children: null,
      });
    }

    return items;
  }, [engineHealth, engineLogs, engineJobHistory, token, triggerLoading, cancellingJobs, queueFilter, jobTabKey, serverTimezone, schedulerStatus, schedulerLoading, formatLogTime, formatActivityTime, getEngineIcon, restartEngine, cancelJob, drainQueue, getEngineTriggerButtons]);

  if (loading && !engineHealth) {
    return (
      <PageLayout active="engineStatus">
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" tip="Loading engine status..." />
        </div>
      </PageLayout>
    );
  }

  const ri = redisInfo;

  return (
    <PageLayout active="engineStatus">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Space>
          <PoweroffOutlined style={{ fontSize: 24 }} />
          <span style={{ fontSize: 20, fontWeight: 600 }}>Engine Status</span>
        </Space>
        <Space size="large">
          {serverTime && (
            <Text type="secondary">
              Server: {new Date(serverTime).toLocaleString()} ({serverTimezone})
            </Text>
          )}
          <Button icon={<ReloadOutlined />} onClick={fetchStatus} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {error && (
        <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {/* Redis Summary - Link to Full Monitoring Page */}
      {ri && (
        <Card
          size="small"
          style={{ marginBottom: 16, cursor: "pointer" }}
          onClick={() => navigate("/core-services/redis")}
          hoverable
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space size="middle" wrap>
              <Space>
                <DatabaseOutlined style={{ color: "#cf1322", fontSize: 18 }} />
                <Text strong>Redis</Text>
              </Space>
              <Tag color={ri.loading ? "warning" : "success"}>{ri.loading ? "Loading" : "Online"}</Tag>
              <Tag>{ri.role === "master" ? "Master" : "Replica"}</Tag>
              <Divider type="vertical" />
              <Space size="large">
                <Tooltip title="Memory">
                  <Space size={4}>
                    <HddOutlined style={{ color: parseFloat(ri.memoryUsagePct) > 80 ? "#ff4d4f" : "#1890ff" }} />
                    <Text>{ri.usedMemoryHuman}</Text>
                    {ri.maxMemoryHuman && <Text type="secondary">/ {ri.maxMemoryHuman}</Text>}
                  </Space>
                </Tooltip>
                <Tooltip title="Operations per second">
                  <Space size={4}>
                    <DashboardOutlined style={{ color: "#722ed1" }} />
                    <Text>{ri.instantaneousOpsPerSec} ops/s</Text>
                  </Space>
                </Tooltip>
                <Tooltip title="Connected clients">
                  <Space size={4}>
                    <TeamOutlined />
                    <Text>{ri.connectedClients} clients</Text>
                  </Space>
                </Tooltip>
                <Tooltip title="Total keys">
                  <Space size={4}>
                    <Text type="secondary">{ri.totalKeys} keys</Text>
                  </Space>
                </Tooltip>
              </Space>
              {/* Warning indicators */}
              {ri.evictedKeys > 0 && (
                <Tag color="error"><ExclamationCircleOutlined /> Evicting!</Tag>
              )}
              {parseFloat(ri.memoryUsagePct) > 80 && (
                <Tag color="warning"><WarningOutlined /> High Memory</Tag>
              )}
              {ri.slowlogLen > 10 && (
                <Tag color="warning">{ri.slowlogLen} slow queries</Tag>
              )}
            </Space>
            <Space>
              <Text type="secondary">View Details</Text>
              <RightOutlined style={{ color: token.colorTextSecondary }} />
            </Space>
          </div>
        </Card>
      )}

      {/* Engine Tabs */}
      <div
        style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, overflow: "hidden" }}
        className="engine-tabs-wrap"
      >
        <style>{`
          .engine-tabs-wrap .ant-tabs-nav-list {
            flex-wrap: wrap !important;
          }
          .engine-tabs-wrap .ant-tabs-tab[data-node-key="__row-break__"] {
            flex-basis: 100% !important;
            height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            overflow: hidden !important;
            pointer-events: none;
            min-height: 0 !important;
            opacity: 0;
            line-height: 0 !important;
          }
          .engine-tabs-wrap .ant-tabs-nav-operations { display: none !important; }
          .engine-tabs-wrap .ant-tabs-ink-bar { display: none !important; }
        `}</style>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setQueueFilter(null);
            setJobTabKey("active");
          }}
          items={tabItems}
          type="card"
          tabBarStyle={{ marginBottom: 0, background: token.colorBgLayout, padding: "8px 8px 0" }}
        />
      </div>

      {/* Aggregation Modal */}
      <Modal
        title="Trigger Aggregation"
        open={aggregationModalOpen}
        onOk={triggerAggregation}
        onCancel={() => setAggregationModalOpen(false)}
        confirmLoading={triggerLoading.aggregation}
        okText="Trigger"
      >
        <p>Select the date to run aggregation for:</p>
        <DatePicker
          style={{ width: "100%" }}
          value={aggregationDate}
          onChange={setAggregationDate}
        />
      </Modal>

      {/* Backfill Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Backfill System Data</span>
          </Space>
        }
        open={backfillModalOpen}
        onCancel={() => setBackfillModalOpen(false)}
        footer={null}
        width={500}
      >
        <Form
          form={backfillForm}
          layout="vertical"
          onFinish={submitBackfill}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="sns"
            label="Select Systems"
            rules={[{ required: true, message: "Please select at least one system" }]}
          >
            <Select
              mode="multiple"
              placeholder="Search and select systems..."
              options={systemOptions}
              loading={systemsLoading}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount={3}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            />
          </Form.Item>

          <Form.Item
            name="timeRange"
            label="Time Range (max 24 hours)"
            rules={[{ required: true, message: "Please select a time range" }]}
          >
            <DatePicker.RangePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%" }}
              disabledDate={(current) => current && current > dayjs()}
            />
          </Form.Item>

          <Alert
            message="Backfill will re-process historical data for the selected systems and time range. This may take a few minutes depending on the amount of data."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setBackfillModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={backfillLoading}>
                Start Backfill
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* AEMO Settlement Modal */}
      <Modal
        title="Trigger AEMO Settlement Sync"
        open={aemoSettlementModalOpen}
        onOk={triggerAemoSettlement}
        onCancel={() => setAemoSettlementModalOpen(false)}
        confirmLoading={triggerLoading.aemoSettlement}
        okText="Trigger Sync"
      >
        <p>Select the settlement date to sync AEMO data for all active configs:</p>
        <DatePicker
          style={{ width: "100%" }}
          value={aemoSettlementDate}
          onChange={setAemoSettlementDate}
          disabledDate={(current) => current && current > dayjs()}
        />
      </Modal>
    </PageLayout>
  );
}
